import {
  Alert,
  Button,
  Col,
  Flex,
  InputNumber,
  InputNumberProps,
  message,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { TbEdit, TbFilter, TbInfoCircle } from "react-icons/tb";
import { ApplicationDto } from "dtos/application.dto";
import { PaymentType, StrategyConfigV2, StrategyFormat } from "dtos/strategy.dto";
import { formatMoney, parseMoney, Safe, toCapitalCase } from "utils/functions";
import { getStrategyContext } from "utils/core/strategy.util";
import { useGenerateStrategy } from "utils/hooks/useGenerateStrategy";
import { defaultHeaders, LedgerTable } from "components/StrategyPreview/Ledger";
import WeeklyPaymentTimeline from "./WeeklyPaymentTimeline";
import PaymentBreakdown from "./PaymentBreakdown";

export type PreviewSection = "metrics" | "overview" | "breakdown" | "timeline" | "ledger";

export const PREVIEW_SECTION_LABELS: Record<PreviewSection, string> = {
  metrics: "Metrics",
  overview: "Overview",
  breakdown: "Breakdown",
  timeline: "Timeline",
  ledger: "Ledger",
};

const Card = ({ title, children, extra }: { title: string; children: ReactNode; extra?: ReactNode }) => (
  <div className="bg-white border border-solid border-gray-200 rounded-xl px-4 py-4">
    <Flex className="justify-between items-center mb-3.5">
      <Typography.Text className="uppercase !text-xs font-semibold tracking-wide !text-gray-500">
        {title}
      </Typography.Text>
      {extra}
    </Flex>
    {children}
  </div>
);

const StatCard = ({ label, text, subtext }: { label: string; text: string; subtext?: string }) => (
  <Flex vertical className="w-full min-h-16">
    <Typography.Text className="!text-[13px] !text-gray-600">{label}</Typography.Text>
    <Typography.Title level={4} className="!m-0">
      {text}
    </Typography.Title>
    {subtext?.length ? <span className="!text-xs !text-gray-500">{subtext}</span> : null}
  </Flex>
);

const EditableStatCard = ({
  label,
  text,
  onChange,
  min,
  subtext,
  ...props
}: Omit<InputNumberProps, "onChange"> & {
  label: string;
  text?: number;
  subtext?: string;
  onChange?: (val: number | string | null) => unknown;
}) => {
  const [localValue, setLocalValue] = useState<number | string | null>(text ?? null);
  const isFocused = React.useRef(false);
  useEffect(() => {
    if (!isFocused.current) setLocalValue(text ?? null);
  }, [text]);

  const commit = (val: number | string | null) => {
    if (!onChange) return;
    if (!val && val !== 0) return onChange(null);
    const num = Number(val);
    if (Number.isNaN(num)) return onChange(null);
    if (typeof min === "number" && num < min) {
      setLocalValue(min);
      onChange(min);
      message.error(`Can not go below ${min}`);
    } else {
      onChange(val);
    }
  };

  return (
    <Flex vertical className="w-full min-h-16">
      <Typography.Text className="!text-[13px] !text-gray-600 flex items-center gap-1">
        {label}
        <Tooltip title="Editable">
          <TbEdit />
        </Tooltip>
      </Typography.Text>
      <InputNumber
        value={localValue}
        onChange={(val) => setLocalValue(val)}
        onFocus={() => {
          isFocused.current = true;
        }}
        onBlur={() => {
          isFocused.current = false;
          commit(localValue);
        }}
        onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
        controls={false}
        variant="borderless"
        className="text-xl font-semibold text-black !p-0 [&_.ant-input-number-input]:!p-0 [&_.ant-input-number-input]:!m-0 [&_.ant-input-number-input]:!h-auto w-full border-0 border-b border-dashed !border-violet-300 !rounded-none"
        {...props}
      />
      {subtext?.length ? <span className="!text-xs !text-gray-500">{subtext}</span> : null}
    </Flex>
  );
};

/**
 * Live preview of a strategy config, compiled on the server.
 * `sections` controls which blocks render so each wizard step can focus on its own output.
 */
const StrategyPreviewSections = ({
  application,
  config,
  sections,
  onWeeklyBudgetChange,
}: {
  application: ApplicationDto;
  config: StrategyConfigV2;
  sections: PreviewSection[];
  /** When provided, the weekly budget in business metrics is editable. */
  onWeeklyBudgetChange?: (value: number | undefined) => void;
}) => {
  const context = useMemo(
    () => getStrategyContext(application, { weekly_budget: application?.requested_budget }),
    [application],
  );
  const generator = useGenerateStrategy(context);
  const query = generator.asyncCompile(config);
  const [paymentTableShowMode, setPaymentTableShowMode] = useState<"detailed" | "compact">("compact");
  const [visibleHeaders, setVisibleHeaders] = useState(defaultHeaders);

  const show = (section: PreviewSection) => sections.includes(section);
  const guardRails = generator.getGuardRails({ ...config, format: StrategyFormat.CUSTOM });
  const weeklyBudget = config.meta?.weekly_budget as number | undefined;

  const metrics = show("metrics") && (
    <Card title="Business metrics">
      <Flex gap={16} vertical>
        <Row gutter={12}>
          <Col span={12}>
            {onWeeklyBudgetChange ? (
              <EditableStatCard
                label="Weekly budget"
                text={weeklyBudget}
                min={guardRails.min_custom_amount}
                onChange={(val) => {
                  const num = Number(val);
                  onWeeklyBudgetChange(!val || Number.isNaN(num) ? undefined : num);
                }}
                formatter={(val) => {
                  if (!val || Number(val) < 1) return "None";
                  return formatMoney(Number(val));
                }}
                parser={(val) => {
                  if (!val || val === "None") return "";
                  return parseMoney(val) ?? "";
                }}
                subtext={`minimum value $${guardRails.min_custom_amount}`}
              />
            ) : (
              <StatCard
                label="Weekly budget"
                text={weeklyBudget ? formatMoney(weeklyBudget) : "None"}
                subtext={`minimum value $${guardRails.min_custom_amount}`}
              />
            )}
          </Col>
          <Col span={12}>
            <StatCard
              label="Current payment"
              text={application?.summary?.weekly_payments ? formatMoney(application.summary.weekly_payments) : "None"}
              subtext="per week"
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <StatCard label="Total lawsuits" text={String(application?.total_lawsuits || 0)} />
          </Col>
          <Col span={12}>
            <StatCard label="Total loans" text={String(application?.total_loans || application?.loans?.length || 0)} />
          </Col>
        </Row>
      </Flex>
    </Card>
  );

  if (query.isPending && !query.data) {
    return (
      <Flex vertical gap={14}>
        {metrics}
        {sections.some((s) => s !== "metrics") && (
          <div className="bg-white border border-solid border-gray-200 rounded-xl flex items-center justify-center py-24">
            <Spin tip="Compiling strategy..." />
          </div>
        )}
      </Flex>
    );
  }

  if (query.isError && !query.data) {
    return (
      <Flex vertical gap={14}>
        {metrics}
        <Alert
          type="error"
          showIcon
          message="Couldn't compile strategy preview"
          description="Try adjusting the values, or check your connection."
        />
      </Flex>
    );
  }

  const { strategy, payment_table, ledger } = query.data;
  const frequency = strategy?.payment_type === PaymentType.MONTHLY ? "month" : "week";
  const perFrequency = frequency === "month" ? 4 : 1;
  const creditorMaxWeekly = Math.max(...(strategy?.common.split_rows.creditor_max_weekly ?? []));

  return (
    <Flex vertical gap={14} className="relative">
      {(query.isFetching || query.isError) && (
        <div className="absolute -top-7 right-0">
          <Tooltip title={query.isError ? "Preview refresh failed, showing last result" : "Refreshing preview..."}>
            {query.isError ? <TbInfoCircle className="text-red-500 text-base" /> : <Spin size="small" />}
          </Tooltip>
        </div>
      )}

      {metrics}

      {show("overview") && (
        <Card title="Strategy overview">
          <Flex gap={16} vertical>
            <Row gutter={12}>
              <Col span={12}>
                <StatCard
                  label="Payable amount"
                  text={formatMoney(strategy?.common.total_payable, true)}
                  subtext={`${formatMoney(strategy?.common.weekly_payments ? strategy.common.weekly_payments * perFrequency : undefined, false)} / ${frequency}`}
                />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Initial payment"
                  text={formatMoney(strategy?.common.initial_payment, true)}
                  subtext={
                    strategy?.common.initial_amount
                      ? `${formatMoney(strategy.common.initial_amount, false)} to be paid upfront`
                      : undefined
                  }
                />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <StatCard
                  label="Total debt"
                  text={formatMoney(strategy?.common.total_debt, true)}
                  subtext={
                    creditorMaxWeekly
                      ? `${formatMoney(Safe(creditorMaxWeekly).multiply(perFrequency).value, false)} / ${frequency}`
                      : "One-Time payment"
                  }
                />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Net commission"
                  text={formatMoney(strategy?.common.interest, true)}
                  subtext={
                    strategy?.common.split_rows.program_weekly
                      ? `${formatMoney(Safe(strategy.common.split_rows.program_weekly).multiply(perFrequency).value, false)} / ${frequency}`
                      : "One-Time payment"
                  }
                />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <StatCard label="Tenure" text={`${Math.floor(strategy.total_weeks / perFrequency)} ${frequency}s`} />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Commission tenure"
                  text={`${Math.floor(strategy.program_tenure / perFrequency)} ${frequency}s`}
                />
              </Col>
            </Row>
          </Flex>
        </Card>
      )}

      {show("breakdown") && (
        <Card title="Payment breakdown">
          <PaymentBreakdown
            total={payment_table.totals.payment}
            commission={payment_table.totals.program_fee}
            creditor={payment_table.totals.creditor}
            service={payment_table.totals.service_fee}
            legal={payment_table.totals.legal_fee}
          />
        </Card>
      )}

      {show("timeline") && (
        <Card title="Weekly payment timeline">
          {strategy.total_weeks > 0 ? (
            <WeeklyPaymentTimeline
              ledgerEntries={ledger}
              initial_weeks={strategy.common.initial_split ?? 1}
              program_weeks={strategy.program_tenure}
            />
          ) : (
            <Typography.Text className="block text-center py-6 !text-gray-500">
              One-time payment — no timeline.
            </Typography.Text>
          )}
        </Card>
      )}

      {show("ledger") && (
        <Card
          title="Payment ledger"
          extra={
            <Space>
              <Button
                size="small"
                disabled={ledger.length === 0}
                onClick={() => setPaymentTableShowMode((prev) => (prev === "compact" ? "detailed" : "compact"))}
              >
                {paymentTableShowMode === "detailed" ? "Compact" : "Detailed"}
              </Button>
              <Select
                mode="multiple"
                value={visibleHeaders}
                onChange={setVisibleHeaders}
                options={defaultHeaders.map((item) => ({ label: toCapitalCase(item), key: item, value: item }))}
                maxTagCount={0}
                tagRender={() => <div />}
                suffixIcon={<TbFilter className="text-black" />}
                size="small"
                className="min-w-8 w-fit h-fit !p-0"
                styles={{ popup: { root: { minWidth: 220 } } }}
              />
            </Space>
          }
        >
          <LedgerTable
            ledgerRows={ledger ?? []}
            paymentTable={payment_table}
            mode={paymentTableShowMode}
            headers={visibleHeaders}
            cycle={strategy?.payment_type === PaymentType.MONTHLY ? "monthly" : "weekly"}
            initialPaymentRange={strategy?.common.initial_split ?? 1}
            interestPaymentRange={strategy?.program_tenure}
          />
        </Card>
      )}
    </Flex>
  );
};

export default StrategyPreviewSections;
